<script lang="ts">
  // Dot plot of clock times across days — inline SVG, no chart library.
  // Ported from the edge server's Stats.svelte timeBandPoints + band markup.
  //
  // Values are minutes since local midnight, one per day. The y axis is the
  // whole 24 hours by default so a 9am cluster reads as "a tight band near
  // the top", which is the point of the card: how repeatable the time is,
  // not what the time is (the title already says that).
  import { accentClasses } from './chartAccent';

  const {
    values,
    labels = [],
    accent = 'teal',
    ariaLabel
  }: {
    /** Minutes since midnight, one per sample, day-ascending. */
    values: number[];
    labels?: string[];
    accent?: string;
    ariaLabel: string;
  } = $props();

  const W = 240;
  const H = 72;
  const DAY_MINUTES = 24 * 60;

  const c = $derived(accentClasses(accent));
  const points = $derived(
    values.map((minute, i) => ({
      x: (i / Math.max(values.length - 1, 1)) * W,
      y: (minute / DAY_MINUTES) * H,
      minute,
      label: labels[i] ?? ''
    }))
  );

  function clock(minute: number): string {
    const m = Math.max(0, Math.min(DAY_MINUTES - 1, Math.round(minute)));
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  }
</script>

{#if values.length > 0}
  <div>
    <svg
      viewBox="0 0 {W} {H}"
      preserveAspectRatio="none"
      class="h-[72px] w-full"
      role="img"
      aria-label="{ariaLabel}: {values.length} days, {clock(Math.min(...values))} to {clock(Math.max(...values))}"
    >
      <!-- 06:00 / 12:00 / 18:00 guides, so a dot's height is readable
           without a full axis. -->
      {#each [6, 12, 18] as hour (hour)}
        <line
          x1="0"
          x2={W}
          y1={(hour * 60 * H) / DAY_MINUTES}
          y2={(hour * 60 * H) / DAY_MINUTES}
          class="stroke-toyama-cloudy-gray"
          stroke-width="0.5"
          stroke-dasharray="4 4"
        />
      {/each}
      {#each points as p, i (i)}
        <circle cx={p.x} cy={p.y} r="3.5" class={c.fill} opacity="0.85">
          <title>{p.label}: {clock(p.minute)}</title>
        </circle>
      {/each}
    </svg>
    <!-- The y axis is the clock and the x axis is just "one dot per day",
         so the caption states the span rather than pretending to be a
         horizontal time axis. -->
    <div class="mt-1 flex justify-between text-[10.5px] text-dark-gray">
      <span>Earliest {clock(Math.min(...values))}</span>
      <span>Latest {clock(Math.max(...values))}</span>
    </div>
  </div>
{/if}
